import React, { useState } from 'react';
import { 
  Layers, Lock, User, Key, ArrowRight, CheckCircle2, 
  AlertCircle, Sparkles, ArrowLeft, Eye, EyeOff, Server,
  Database, HardDrive, Cpu, ChevronRight
} from 'lucide-react';
import { AUTH_CREDENTIALS, USER_ROLES } from '../../../data/lmsAuthData';

export const ContentManagerAuthPage = ({ onLoginSuccess, setActiveView }) => {
  const managerPreset = AUTH_CREDENTIALS.find(c => c.role === USER_ROLES.CONTENT_MANAGER) || AUTH_CREDENTIALS[1];

  const [username, setUsername] = useState(managerPreset.demoUsername);
  const [password, setPassword] = useState(managerPreset.demoPassword);
  const [cdnCluster, setCdnCluster] = useState('edge-west-africa-01');
  const [showPassword, setShowPassword] = useState(false);
  const [authError, setAuthError] = useState('');
  const [isAuthenticating, setIsAuthenticating] = useState(false);

  const handle1ClickFastPass = () => {
    setUsername(managerPreset.demoUsername);
    setPassword(managerPreset.demoPassword);
    setAuthError('');
    onLoginSuccess(managerPreset);
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    setIsAuthenticating(true);
    setAuthError('');

    setTimeout(() => {
      setIsAuthenticating(false);
      const cleanUser = username.trim().toLowerCase();
      const isMatch = (cleanUser === managerPreset.demoUsername.toLowerCase() || 
                       cleanUser === managerPreset.email.toLowerCase() ||
                       cleanUser.includes('content') || cleanUser.includes('manager')) &&
                      (password === managerPreset.demoPassword || password.length >= 4);

      if (isMatch) {
        onLoginSuccess(managerPreset);
      } else {
        setAuthError('CMS Master authentication failed. Please verify credentials or utilize the 1-Click Fast Pass below.');
      }
    }, 450);
  };

  return (
    <div className="min-h-screen bg-[#030F1C] text-slate-100 flex flex-col justify-between relative overflow-hidden py-8">
      {/* Cyan Ambient Glow */}
      <div className="absolute top-10 left-1/4 w-96 h-96 bg-cyan-600/15 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-10 right-10 w-96 h-96 bg-[#0B2644]/50 rounded-full blur-3xl pointer-events-none" />

      {/* Top Breadcrumb Nav */}
      <div className="max-w-4xl mx-auto px-4 w-full mb-6">
        <div className="flex items-center justify-between">
          <button
            onClick={() => setActiveView('lms-hub')}
            className="flex items-center space-x-1.5 px-3 py-1.5 rounded-xl bg-[#0B2644] hover:bg-[#7A470E] text-white text-xs font-bold font-sc transition-colors border border-slate-700/80 cursor-pointer shadow-xs"
          >
            <ArrowLeft className="w-3.5 h-3.5 text-amber-300" />
            <span>← Back to Central Portals</span>
          </button>

          <div className="flex items-center space-x-2 text-xs font-mono bg-cyan-950/80 border border-cyan-500/40 text-cyan-300 px-3 py-1.5 rounded-full">
            <Server className="w-3.5 h-3.5" />
            <span>Tier-1 Courseware CMS Console · Master Key</span>
          </div>
        </div>
      </div>

      {/* Main Authentication Card */}
      <div className="max-w-4xl mx-auto px-4 w-full flex-1 flex flex-col justify-center">
        <div className="bg-gradient-to-b from-[#082038] via-[#06182B] to-[#04101D] border-2 border-cyan-500/40 rounded-3xl p-6 sm:p-10 shadow-2xl">
          
          {/* Header */}
          <div className="text-center max-w-2xl mx-auto space-y-3 mb-8">
            <div className="inline-flex items-center justify-center p-3.5 bg-cyan-950/60 rounded-2xl border-2 border-cyan-400 text-cyan-300 shadow-xl">
              <Layers className="w-10 h-10" />
            </div>
            <div>
              <span className="text-xs font-mono font-bold uppercase tracking-widest text-cyan-400">
                Academic Computing & E-Learning Architecture
              </span>
              <h1 className="text-2xl sm:text-4xl font-black font-sc text-white tracking-wide mt-1">
                LMS Global Content Manager
              </h1>
              <p className="text-xs sm:text-sm text-slate-300 font-light mt-1">
                Master console for courseware management, video CDN ingestion, and cross-programme syllabus publishing.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            
            {/* Form Column */}
            <div className="lg:col-span-7 bg-[#04101D]/80 p-6 sm:p-7 rounded-2xl border border-slate-800">
              <form onSubmit={handleFormSubmit} className="space-y-4">
                {authError && (
                  <div className="p-3 bg-red-950/80 border border-red-500/50 rounded-xl text-xs text-red-200 flex items-start space-x-2">
                    <AlertCircle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                    <span>{authError}</span>
                  </div>
                )}

                {/* Content Manager Email */}
                <div>
                  <label className="block text-xs font-bold font-sc text-slate-200 mb-1.5">
                    CMS Administrator Email / ID:
                  </label>
                  <div className="relative">
                    <User className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                    <input
                      type="text"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      placeholder="content.manager@businessschool.aksu.edu.ng"
                      required
                      className="w-full pl-10 pr-4 py-2.5 bg-[#06182B] border border-slate-700 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400 transition-colors"
                    />
                  </div>
                </div>

                {/* Master Repository Key */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-xs font-bold font-sc text-slate-200">
                      Master Repository Key / Password:
                    </label>
                    <span className="text-[11px] text-cyan-300/80 font-mono">Global Admin Clearance</span>
                  </div>
                  <div className="relative">
                    <Lock className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••••••"
                      required
                      className="w-full pl-10 pr-10 py-2.5 bg-[#06182B] border border-slate-700 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400 transition-colors"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {/* CDN Node Cluster */}
                <div>
                  <label className="block text-xs font-bold font-sc text-slate-200 mb-1.5">
                    Streaming CDN Gateway Cluster:
                  </label>
                  <div className="relative">
                    <HardDrive className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                    <select
                      value={cdnCluster}
                      onChange={(e) => setCdnCluster(e.target.value)}
                      className="w-full pl-10 pr-4 py-2.5 bg-[#06182B] border border-slate-700 rounded-xl text-xs text-slate-200 focus:outline-none focus:border-cyan-400"
                    >
                      <option value="edge-west-africa-01">West Africa Edge CDN 01 (Akwa Ibom / Lagos)</option>
                      <option value="cloud-main-repo">AKSU Main Campus High-Bandwidth Cloud Node</option>
                      <option value="global-mirror-eu">European Educational Mirror Cluster</option>
                    </select>
                  </div>
                </div>

                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={isAuthenticating}
                    className="w-full py-3 px-4 rounded-xl bg-gradient-to-r from-cyan-700 via-cyan-800 to-[#0B2644] hover:from-cyan-600 hover:to-[#06182B] text-white font-sc font-bold text-xs flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl transition-all cursor-pointer disabled:opacity-50"
                  >
                    <span>{isAuthenticating ? 'Connecting to Courseware Node...' : 'Authenticate & Enter CMS Console'}</span>
                    <ArrowRight className="w-4 h-4 text-cyan-300" />
                  </button>
                </div>
              </form>
            </div>

            {/* Right Column: Persona Profile & Fast Pass */}
            <div className="lg:col-span-5 space-y-4">
              
              {/* Evaluator Fast Pass */}
              <div className="bg-[#0B2644]/40 p-5 rounded-2xl border border-cyan-400/40">
                <div className="flex items-center space-x-2 text-xs font-bold font-sc text-cyan-300 mb-2">
                  <Sparkles className="w-4 h-4 text-cyan-400" />
                  <span>Evaluator 1-Click Fast Pass:</span>
                </div>
                <p className="text-[11px] text-slate-300 mb-4 leading-relaxed">
                  Bypass manual typing. Click below to sign in directly as the LMS Global Content Manager:
                </p>

                <button
                  type="button"
                  onClick={handle1ClickFastPass}
                  className="w-full text-left p-3 rounded-xl bg-[#06182B] hover:bg-[#0B2E54] border border-cyan-500/50 hover:border-cyan-300 transition-all flex items-center justify-between group cursor-pointer shadow-md"
                >
                  <div className="flex items-center space-x-3">
                    <img 
                      src={managerPreset.avatar} 
                      alt={managerPreset.name} 
                      className="w-10 h-10 rounded-xl object-cover border border-cyan-400" 
                    />
                    <div>
                      <div className="text-xs font-bold text-white group-hover:text-cyan-200 font-sc">
                        {managerPreset.name}
                      </div>
                      <div className="text-[10px] text-cyan-300/90 font-mono">
                        {managerPreset.qualifications} · {managerPreset.title}
                      </div>
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-cyan-400 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>

              {/* Technical Telemetry */}
              <div className="p-4 rounded-2xl bg-[#06182B] border border-slate-800 space-y-2 text-xs">
                <span className="font-bold text-slate-200 block font-sc">
                  CMS Architecture Status:
                </span>
                <div className="space-y-1.5 text-[11px] font-mono text-slate-400">
                  <div className="flex justify-between items-center">
                    <span>Courseware Repos:</span>
                    <span className="text-emerald-400 font-bold">6 Programmes Active</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span>Streaming CDN:</span>
                    <span className="text-cyan-300 font-bold">100% Uptime (99.98%)</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span>Video Processing:</span>
                    <span className="text-amber-300 font-bold">Transcoder Ready</span>
                  </div>
                </div>
              </div>

            </div>

          </div>

        </div>
      </div>

      {/* Footer */}
      <div className="max-w-4xl mx-auto px-4 w-full mt-6 text-center text-xs text-slate-400">
        <span className="font-mono text-[11px]">
          Akwa Ibom State University Executive Business School · Directorate of Academic Computing
        </span>
      </div>
    </div>
  );
};

export default ContentManagerAuthPage;
